import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { get } from "lodash";
import { WebEngage, GA, PGAD } from "../../../integrations";
import { PAGE_TYPE } from "../../../constants";
import styles from "./styles.m.css";
import { fetchTK } from "../../../api";
import { Icon } from "../../atoms/icon";

const ThirukuralPage = ({ data }) => {
  const [selectedPaal, setSelectedPaal] = useState("");
  const [selectedIyal, setSelectedIyal] = useState("");
  const [selectedAdhikaram, setSelectedAdhikaram] = useState("");
  const [thirukuralStructure, setThirukuralStructure] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredKurals, setFilteredKurals] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [kuralData, setKuralData] = useState([]);
  // const [thirukuralData, setThirukuralData] = useState([]);
  const [kuralMapping, setKuralMapping] = useState({}); // Dynamic mapping
  const kuralsPerPage = 10;

  // Dynamic function to create kural to adhikaram mapping from JSON structure
  const createKuralMapping = structureData => {
    const mapping = {};
    const reverseMapping = {}; // For adhikaram to kural range mapping
    let currentKuralNumber = 1;
    let adhikaramCounter = 1;

    if (!structureData || !structureData[0] || !structureData[0].section) {
      return { mapping: {}, reverseMapping: {} };
    }

    structureData[0].section.detail.forEach(paal => {
      paal.chapterGroup.detail.forEach(iyal => {
        iyal.chapters.detail.forEach(adhikaram => {
          const startKural = currentKuralNumber;
          const endKural = currentKuralNumber + 9; // Each adhikaram has 10 kurals

          // Store reverse mapping for quick lookup
          reverseMapping[adhikaramCounter] = {
            start: startKural,
            end: endKural,
            paal: paal.name,
            iyal: iyal.name,
            adhikaram: adhikaram.name,
            adhikaramNumber: adhikaramCounter
          };

          // Map each individual kural number
          for (let kuralNum = startKural; kuralNum <= endKural; kuralNum++) {
            mapping[kuralNum] = {
              adhikaram: adhikaram.name,
              paal: paal.name,
              iyal: iyal.name,
              adhikaramStart: startKural,
              adhikaramEnd: endKural,
              adhikaramNumber: adhikaramCounter
            };
          }

          currentKuralNumber += 10;
          adhikaramCounter += 1;
        });
      });
    });

    return { mapping, reverseMapping };
  };

  // Dynamic function to get adhikaram for a kural number
  const getAdhikaramForKural = kuralNumber => {
    return kuralMapping[kuralNumber].adhikaram || "Unknown";
  };

  // Initialize Thirukural data and structure
  useEffect(() => {
    fetchTK().then(data => {
      const kData = get(data, ["kural"], []);
      const kList = get(data, ["items"], []);

      console.log("Fetched kural data count:", kData.length);
      console.log("Structure data:", kList);

      // Create dynamic mappings
      const { mapping } = createKuralMapping(kList);
      setKuralMapping(mapping);
      // setStructureList(kList);

      console.log("Created mapping for kurals:", Object.keys(mapping).length);
      console.log(
        "Sample mapping:",
        Object.keys(mapping)
          .slice(0, 20)
          .map(k => ({
            kural: k,
            adhikaram: mapping[k].adhikaram
          }))
      );

      setKuralData(kData);
      const organizedData = organizeThirukuralData(kData, kList);
      setThirukuralStructure(organizedData);
      setFilteredKurals(kData);
    });
  }, []);

  // Function to organize Thirukural data into hierarchical structure
  const organizeThirukuralData = (kurals, list) => {
    const structure = {};

    if (!list || !list[0] || !list[0].section) {
      return structure;
    }

    // Build structure from JSON data
    list[0].section.detail.forEach(paal => {
      structure[paal.name] = {};

      paal.chapterGroup.detail.forEach(iyal => {
        structure[paal.name][iyal.name] = {};

        iyal.chapters.detail.forEach(adhikaram => {
          structure[paal.name][iyal.name][adhikaram.name] = [];
        });
      });
    });

    // Map kural numbers to their respective sections
    kurals.forEach(kural => {
      const kuralData = {
        number: kural.Number,
        couplet: `${kural.Line1} ${kural.Line2}`,
        explanation: kural.mv || kural.explanation || "",
        translation: kural.Translation || "",
        transliteration1: kural.transliteration1 || "",
        transliteration2: kural.transliteration2 || "",
        sp: kural.sp || "",
        mk: kural.mk || "",
        Line1: kural.Line1,
        Line2: kural.Line2
      };

      // Use dynamic mapping to find correct location
      const mappingInfo = kuralMapping[kural.Number];
      if (mappingInfo) {
        const paalName = mappingInfo.paal;
        const iyalName = mappingInfo.iyal;
        const adhikaramName = mappingInfo.adhikaram;

        // Add to structure if path exists
        if (structure[paalName] && structure[paalName][iyalName] && structure[paalName][iyalName][adhikaramName]) {
          structure[paalName][iyalName][adhikaramName].push(kuralData);
        }
      }
    });
    return structure;
  };

  const paals = Object.keys(thirukuralStructure);
  const iyals = selectedPaal && thirukuralStructure[selectedPaal] ? Object.keys(thirukuralStructure[selectedPaal]) : [];
  const adhikarams =
    selectedIyal && thirukuralStructure[selectedPaal] && thirukuralStructure[selectedPaal][selectedIyal]
      ? Object.keys(thirukuralStructure[selectedPaal][selectedIyal])
      : [];

  React.useEffect(() => {
    WebEngage.registerPageView();
    GA.registerPageView(PAGE_TYPE.THIRUKURAL_PAGE, { collection: data.collection });
    PGAD.loadScript();
  }, []);

  // Filter kurals only when all three selections are made
  const filterKurals = () => {
    // Only filter when all three dropdowns are selected
    if (selectedPaal && selectedIyal && selectedAdhikaram) {
      const filtered = kuralData.filter(kural => {
        const mappingInfo = kuralMapping[kural.Number];
        return (
          mappingInfo &&
          mappingInfo.paal === selectedPaal &&
          mappingInfo.iyal === selectedIyal &&
          mappingInfo.adhikaram === selectedAdhikaram
        );
      });

      console.log(`Filtered kurals for ${selectedAdhikaram}:`, filtered.length);
      console.log(
        "Sample filtered kurals:",
        filtered.slice(0, 3).map(k => ({ number: k.Number, adhikaram: getAdhikaramForKural(k.Number) }))
      );

      setFilteredKurals(filtered);
      setIsFiltered(true);
      setCurrentPage(1);
    } else {
      // Show all kurals when not all dropdowns are selected
      setFilteredKurals(kuralData);
      setIsFiltered(false);
      setCurrentPage(1);
    }
  };

  // Effect to trigger filtering when selections change
  useEffect(
    () => {
      if (Object.keys(kuralMapping).length > 0) {
        // Only filter when mapping is ready
        filterKurals();
      }
    },
    [selectedPaal, selectedIyal, selectedAdhikaram, kuralData, kuralMapping]
  );

  const handlePaalChange = paal => {
    setSelectedPaal(paal);
    setSelectedIyal("");
    setSelectedAdhikaram("");
    // Don't filter here - let the useEffect handle it
  };

  const handleIyalChange = iyal => {
    setSelectedIyal(iyal);
    setSelectedAdhikaram("");
    // Don't filter here - let the useEffect handle it
  };

  const handleAdhikaramChange = adhikaram => {
    setSelectedAdhikaram(adhikaram);
    // Don't filter here - let the useEffect handle it
  };

  // const handleKuralExplanation = kuralNumber => {
  //   const adhikaramName = getAdhikaramForKural(kuralNumber);
  //   const urlFriendlyAdhikaram = adhikaramName.replace(/\s+/g, "-");
  //   window.location.href = `/thirukural/${urlFriendlyAdhikaram}/${kuralNumber}`;
  // };

  const handleKuralExplanation = kuralNumber => {
    const mappingInfo = kuralMapping[kuralNumber];
    if (!mappingInfo) return;

    window.location.href = `/thirukural/${mappingInfo.adhikaramNumber}/${kuralNumber}`;
  };

  const handlePageChange = pageNumber => {
    setCurrentPage(pageNumber);
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredKurals.length / kuralsPerPage);
  const startIndex = (currentPage - 1) * kuralsPerPage;
  const endIndex = startIndex + kuralsPerPage;
  const currentKurals = filteredKurals.slice(startIndex, endIndex);

  return (
    <React.Fragment>
      <div className={`main-page styles-m__base__2vtxy js-story-main-container ${styles["base-class"]}`}>
        <div className="page-content infinity-page">
          <div className={`${styles["story-grid"]}`}>
            <div className={`${styles["main-container"]}`}>
              <div className={`${styles["thirukural-div"]}`}>
                <div className="container">
                  <div className={`${styles["thirukural-header"]}`}>
                    <div>
                      <img src="https://images.assettype.com/vikatan/2025-08-29/r7mga4i5/thiruvalluvar.png" alt="" />
                    </div>
                    <h1>திருக்குறள்</h1>
                    <p>
                      தமிழ் <br />
                      இலக்கியத்தின் மணி
                    </p>
                  </div>

                  {/* Hierarchical Navigation */}
                  <div className={`${styles["hierarchy-nav"]}`}>
                    {/* Paal Selection */}
                    <div className={`${styles["nav-level"]}`}>
                      <label>குறள் பால்:</label>
                      <select
                        value={selectedPaal}
                        onChange={e => handlePaalChange(e.target.value)}
                        className={`${styles["nav-select"]}`}
                      >
                        <option value="">அனைத்து குறள்களும்</option>
                        {paals.map((paal, index) => (
                          <option key={index} value={paal}>
                            {paal}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Iyal Selection */}
                    <div className={`${styles["nav-level"]}`}>
                      <label>குறள் இயல்:</label>
                      <select
                        value={selectedIyal}
                        onChange={e => handleIyalChange(e.target.value)}
                        className={`${styles["nav-select"]}`}
                        disabled={!selectedPaal}
                      >
                        <option value="">இயல் தேர்ந்தெடுக்கவும்</option>
                        {iyals.map((iyal, index) => (
                          <option key={index} value={iyal}>
                            {iyal}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Adhikaram Selection */}
                    <div className={`${styles["nav-level"]}`}>
                      <label>அதிகாரம்:</label>
                      <select
                        value={selectedAdhikaram}
                        onChange={e => handleAdhikaramChange(e.target.value)}
                        className={`${styles["nav-select"]}`}
                        disabled={!selectedIyal}
                      >
                        <option value="">அதிகாரம் தேர்ந்தெடுக்கவும்</option>
                        {adhikarams.map((adhikaram, index) => (
                          <option key={index} value={adhikaram}>
                            {adhikaram}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Kural List Display with Pagination */}
                  <div className={`${styles["kural-list-display"]}`}>
                    <div className={`${styles["adhikaram-header-section"]}`}>
                      <div>
                        <img src="https://images.assettype.com/vikatan/2025-08-29/r7mga4i5/thiruvalluvar.png" alt="" />
                      </div>
                      <div className={`${styles["adhikaram-header"]}`}>
                        <h2>
                          {isFiltered
                            ? `${selectedPaal}${selectedIyal ? ` → ${selectedIyal}` : ""}${
                                selectedAdhikaram ? ` → ${selectedAdhikaram}` : ""
                              }`
                            : "அனைத்து திருக்குறள்களும்"}
                        </h2>
                        <p className={`${styles["hierarchy-path"]}`}>மொத்தம் {filteredKurals.length} குறள்கள்</p>
                      </div>
                    </div>

                    <div className={`${styles["kural-list"]}`}>
                      {currentKurals.map((kural, index) => (
                        <div key={index} className={`${styles["kural-item"]}`}>
                          <div className={`${styles["kural-header-row"]}`}>
                            <div className={`${styles["kural-number"]}`}>
                              <span className={`${styles["kural-number-count"]}`}>{kural.Number}</span>
                            </div>
                            <div className={`${styles["kural-couplet"]}`}>
                              {kural.Line1} {kural.Line2}
                            </div>
                            <button
                              onClick={() => handleKuralExplanation(kural.Number)}
                              className={`${styles["explanation-btn"]}`}
                            >
                              குறள் விளக்கம்
                              <Icon type={"slider-next"} className={`${styles["kural-next-icon"]}`} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className={`${styles["pagination"]}`}>
                      {/* Page info */}
                      <div className={`${styles["page-info"]}`}>
                        பக்கம் {currentPage} / {totalPages} ({startIndex + 1}-
                        {Math.min(endIndex, filteredKurals.length)} of {filteredKurals.length})
                      </div>

                      <div className={`${styles["page-controls"]}`}>
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className={`${styles["pagination-btn"]}`}
                        >
                          <Icon type={"slider-next"} className={`${styles["prev-icon"]}`} /> முந்தைய
                        </button>

                        {/* Page Numbers with smart pagination */}
                        <div className={`${styles["page-numbers"]}`}>
                          {(() => {
                            let pages = [];
                            const isSmallMobile = window.innerWidth <= 360; // very small devices
                            const isMobile = window.innerWidth > 360 && window.innerWidth <= 768; // normal mobiles

                            // Different limits based on device size
                            const maxVisiblePages = isSmallMobile ? 4 : isMobile ? 5 : 10;

                            if (totalPages <= maxVisiblePages) {
                              // Show all pages if total is less than or equal to maxVisiblePages
                              for (let i = 1; i <= totalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              // Smart pagination logic
                              const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                              const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                              // Add first page
                              if (startPage > 1) {
                                pages.push(1);
                                if (startPage > 2) pages.push("...");
                              }

                              // Add middle pages
                              for (let i = startPage; i <= endPage; i++) {
                                pages.push(i);
                              }

                              // Add last page
                              if (endPage < totalPages) {
                                if (endPage < totalPages - 1) pages.push("...");
                                pages.push(totalPages);
                              }
                            }

                            return pages.map((pageNum, index) => {
                              if (pageNum === "...") {
                                return (
                                  <span key={`ellipsis-${index}`} className={`${styles["page-ellipsis"]}`}>
                                    ...
                                  </span>
                                );
                              }

                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => handlePageChange(pageNum)}
                                  className={`${styles["page-number"]} ${
                                    currentPage === pageNum ? styles["active-page"] : ""
                                  }`}
                                >
                                  <span className={`${styles["page-number-count"]}`}>{pageNum}</span>
                                </button>
                              );
                            });
                          })()}
                        </div>

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className={`${styles["pagination-btn"]}`}
                        >
                          அடுத்து
                          <Icon type={"slider-next"} className={`${styles["next-icon"]}`} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export { ThirukuralPage };

ThirukuralPage.propTypes = {
  data: PropTypes.shape()
};
